import logger from "./logger";
import posthog from "posthog-js";
/**
 * Tracking Scripts Manager
 * Dynamic loading of tracking pixels based on user consent
 */

// Tracking IDs from the codebase
const GTM_ID = 'GTM-K3TX98Z4';
const META_PIXEL_ID = '8773315709379941';
const TIKTOK_PIXEL_ID = 'CRQ0C3JC77U40MO9G7E0';

// Track which scripts have been loaded to prevent duplicates
const loadedScripts = {
  gtm: false,
  metaPixel: false,
  tiktokPixel: false,
};

/**
 * Load Google Tag Manager
 */
export const loadGTM = () => {
  if (loadedScripts.gtm) return;
  
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
    
    loadedScripts.gtm = true;
  } catch (err) {
    logger.error('Error loading GTM:', err);
  }
};

/**
 * Load Meta (Facebook) Pixel
 */
export const loadMetaPixel = () => {
  if (loadedScripts.metaPixel) return;
  
  try {
    // Initialize fbq
    const fbq = function() {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    
    if (!window._fbq) window._fbq = fbq;
    window.fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    
    // Load the script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
    
    // Initialize pixel
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    
    loadedScripts.metaPixel = true;
  } catch (err) {
    logger.error('Error loading Meta Pixel:', err);
  }
};

/**
 * Load TikTok Pixel
 */
export const loadTikTokPixel = () => {
  if (loadedScripts.tiktokPixel) return;
  
  try {
    window.TiktokAnalyticsObject = 'ttq';
    const ttq = window.ttq = window.ttq || [];
    
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
      'holdConsent', 'revokeConsent', 'grantConsent'
    ];
    
    ttq.setAndDefer = function(t, e) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    
    ttq.instance = function(t) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(e, ttq.methods[n]);
      }
      return e;
    };
    
    ttq.load = function(e, n) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = r + '?sdkid=' + e + '&lib=ttq';
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    };
    
    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
    
    loadedScripts.tiktokPixel = true;
  } catch (err) {
    logger.error('Error loading TikTok Pixel:', err);
  }
};

/**
 * Enable PostHog full tracking mode (call after consent)
 * Upgrades from cookieless memory mode to full persistent tracking
 */
export const enablePostHogTracking = () => {
  try {
    // Upgrade persistence from memory to localStorage+cookie after consent
    posthog.set_config({
      persistence: 'localStorage+cookie',
    });
    // Start session recording now that we have consent
    posthog.startSessionRecording();
  } catch (err) {
    logger.error('Error enabling PostHog:', err);
  }
};

/**
 * Disable PostHog persistent tracking (explicit rejection)
 * Reverts to memory-only mode — basic anonymous analytics still work
 * but nothing is stored on the user's device
 */
export const disablePostHogTracking = () => {
  try {
    posthog.set_config({
      persistence: 'memory',
    });
    posthog.stopSessionRecording();
  } catch (err) {
    logger.error('Error disabling PostHog:', err);
  }
};

/**
 * Send GTM consent signals
 */
export const updateGTMConsent = (analytics, marketing) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'consent_update',
      'analytics_storage': analytics ? 'granted' : 'denied',
      'ad_storage': marketing ? 'granted' : 'denied',
    });
  } catch (err) {
    logger.error('Error updating GTM consent:', err);
  }
};

/**
 * Set default GTM consent (denied by default)
 */
export const setGTMDefaultConsent = () => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'consent_default',
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'functionality_storage': 'granted',
      'personalization_storage': 'denied',
      'security_storage': 'granted'
    });
  } catch (err) {
    logger.error('Error setting GTM default consent:', err);
  }
};

/**
 * Load all scripts based on consent
 */
export const loadScriptsBasedOnConsent = (consent) => {
  // Set default consent first
  setGTMDefaultConsent();
  
  if (consent.analytics) {
    // Enable PostHog full tracking
    enablePostHogTracking();
    
    // Load GTM for analytics
    loadGTM();
    
    // Update GTM consent
    updateGTMConsent(true, consent.marketing);
  }
  
  if (consent.marketing) {
    // Load marketing pixels
    loadMetaPixel();
    loadTikTokPixel();
    
    // Make sure GTM is loaded for marketing tags too
    loadGTM();
    
    // Update GTM consent
    updateGTMConsent(consent.analytics, true);
  }
};

/**
 * Track Meta Pixel event (if loaded)
 */
export const trackMetaEvent = (eventName, params = {}) => {
  if (window.fbq && loadedScripts.metaPixel) {
    window.fbq('track', eventName, params);
  }
};

/**
 * Track TikTok event (if loaded)
 */
export const trackTikTokEvent = (eventName, params = {}) => {
  if (window.ttq && loadedScripts.tiktokPixel) {
    window.ttq.track(eventName, params);
  }
};
